import { Alert, Button } from '@yoast/ui-library';
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getGetBrandsQueryKey,
  getGetCurrentUserQueryKey,
  useGetCurrentUser,
  usePostTrialAnalysis,
} from '../../api/generated/insights';
import type { Brand } from '../../api/generated/insights.schemas';
import { useToast } from '../../hooks';

interface AlertStartTrialAnalysisProps {
  brand: Brand;
}

const AlertStartTrialAnalysis: React.FC<AlertStartTrialAnalysisProps> = ({
  brand,
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isStarted, setIsStarted] = useState(false);

  const { data: currentUserData } = useGetCurrentUser();
  const isEligibleForFreeTrial =
    currentUserData?.data?.isEligibleForFreeTrial === true;

  const { mutate, isPending } = usePostTrialAnalysis({
    mutation: {
      onSuccess: () => {
        setIsStarted(true);
        queryClient.invalidateQueries({
          queryKey: getGetCurrentUserQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: getGetBrandsQueryKey(),
        });
      },
      onError: () => {
        toast('Something went wrong starting your free analysis. Please try again.', {
          variant: 'error',
        });
      },
    },
  });

  if (isStarted) {
    return (
      <Alert className="yst-mt-0" variant="info">
        <div className="yst-max-w-xl yst-space-y-2">
          <h3 className="yst-font-medium">
            Your free trial analysis has started!
          </h3>
          <p>
            We&rsquo;re analyzing &ldquo;{brand.name}&rdquo; now. This
            usually takes a few minutes &mdash; we&rsquo;ll email you when
            it&rsquo;s ready.
          </p>
        </div>
      </Alert>
    );
  }

  const hasPendingSetup =
    brand.setupStatus === 'pendingQuerySelection' ||
    brand.setupStatus === 'firstAnalysisInProgress';

  if (!isEligibleForFreeTrial || hasPendingSetup) {
    return null;
  }

  if (brand.setupStatus !== 'firstAnalysisNotScheduled') {
    return null;
  }

  return (
    <Alert className="yst-mt-0">
      <div className="yst-max-w-xl yst-space-y-4">
        <div className="yst-space-y-2">
          <h3 className="yst-font-medium">
            Use your free trial on this brand?
          </h3>
          <p>
            You are eligible for a free brand insights analysis. If you&rsquo;d
            rather analyze a different brand than &ldquo;{brand.name}&rdquo;, you can delete
            this brand in the settings and create a new one.
          </p>
        </div>
        <div className="yst-flex yst-justify-start">
          <Button
            variant="primary"
            onClick={() => mutate({ brandId: brand.id })}
            disabled={isPending}
          >
            {isPending ? 'Starting analysis\u2026' : 'Use the free trial'}
          </Button>
        </div>
      </div>
    </Alert>
  );
};

export default AlertStartTrialAnalysis;
